import { Module } from 'vuex';
import { AxiosResponse } from 'axios';
import {
  SET_SUPPORT_SESSION,
  SET_SUPPORT_SESSION_ISLOADING,
  SET_SUPPORT_SESSION_LAUNCHER,
  SET_SUPPORT_SESSION_PATH,
  SET_SUPPORT_SESSION_VERIFIED,
} from '@/store/mutation_types';
import {
  CHECK_SUPPORT_SESSION,
  CLOSE_SUPPORT_SESSION_LAUNCHER,
  DISMISS_SUPPORT_SESSION,
  END_SUPPORT_SESSION,
  NOTE_SUPPORT_SESSION_PATH,
  OPEN_SUPPORT_SESSION_LAUNCHER,
  RELEASE_SUPPORT_SESSION,
  START_SUPPORT_SESSION,
} from '@/store/action_types';
import SupportSessionService from '@/services/support_session';
import { SupportSessionRecord } from '@/utils/support_session';

// The support session (see utils/support_session): at most one at a time. The
// session itself is persisted, so a reload of the admin brings the desktop
// back — once the API has confirmed it is still open (`verified`). The
// launcher (the organization a session is being launched for), the busy flag
// and `verified` are not persisted.

const UNREACHABLE = "Couldn't reach the server. Check your connection and try again.";

const supportSession: Module<any, {}> = {
  state: {
    supportSession: {
      session: null as SupportSessionRecord | null,
      verified: false,
      launcher: null,
      isLoading: false,
    },
  },
  getters: {
    supportSession: (state: any) => state.supportSession.session,
    supportSessionVerified: (state: any) => state.supportSession.verified,
    supportSessionLauncher: (state: any) => state.supportSession.launcher,
    supportSessionIsLoading: (state: any) => state.supportSession.isLoading,
  },
  mutations: {
    [SET_SUPPORT_SESSION]: (state: any, session: SupportSessionRecord | null) => {
      state.supportSession.session = session;
      if (!session) state.supportSession.verified = false;
    },
    [SET_SUPPORT_SESSION_VERIFIED]: (state: any, verified: boolean) => {
      state.supportSession.verified = verified;
    },
    [SET_SUPPORT_SESSION_PATH]: (state: any, path: string) => {
      if (state.supportSession.session) state.supportSession.session.lastPath = path;
    },
    [SET_SUPPORT_SESSION_LAUNCHER]: (state: any, organization: any) => {
      state.supportSession.launcher = organization;
    },
    [SET_SUPPORT_SESSION_ISLOADING]: (state: any, isLoading: boolean) => {
      state.supportSession.isLoading = isLoading;
    },
  },
  actions: {
    // Opens the launch dialog for an organization (from its page, or the list).
    [OPEN_SUPPORT_SESSION_LAUNCHER]: ({ commit }: any, organization: any) => {
      commit(SET_SUPPORT_SESSION_LAUNCHER, organization || null);
    },
    [CLOSE_SUPPORT_SESSION_LAUNCHER]: ({ commit }: any) => {
      commit(SET_SUPPORT_SESSION_LAUNCHER, null);
    },
    // Asks the API for a session; resolves to whether it started, with the
    // API's words when it didn't (for the dialog to show).
    [START_SUPPORT_SESSION]: async ({ commit }: any, payload: { organization: any; app: string }) => {
      commit(SET_SUPPORT_SESSION_ISLOADING, true);
      try {
        const result: AxiosResponse = await SupportSessionService.start({
          organizationId: payload.organization.id,
          app: payload.app,
        });
        const body = result && result.data;
        if (body && body.status && body.sessionToken && body.data) {
          const account = body.data;
          const info = account.supportSession || {};
          const session: SupportSessionRecord = {
            id: info.id,
            organizationId: payload.organization.id,
            organizationName: info.organizationName || payload.organization.name,
            organizationLogo: payload.organization.logo || null,
            app: info.app || payload.app,
            role: info.role || 'admin',
            startedAt: info.startedAt || new Date().toISOString(),
            expiresAt: info.expiresAt,
            sessionToken: body.sessionToken,
            account,
            lastPath: null,
          };
          commit(SET_SUPPORT_SESSION, session);
          commit(SET_SUPPORT_SESSION_VERIFIED, true);
          commit(SET_SUPPORT_SESSION_LAUNCHER, null);
          return { ok: true, message: '' };
        }
        return { ok: false, message: (body && body.message) || 'The session could not be started. Try again.' };
      } catch (error) {
        return { ok: false, message: UNREACHABLE };
      } finally {
        commit(SET_SUPPORT_SESSION_ISLOADING, false);
      }
    },
    // The page the app is on, kept with the session.
    [NOTE_SUPPORT_SESSION_PATH]: ({ commit, state }: any, path: string) => {
      const session = state.supportSession.session;
      if (session && typeof path === 'string' && path && session.lastPath !== path) {
        commit(SET_SUPPORT_SESSION_PATH, path);
      }
    },
    // Ends the session with the API, then closes the desktop. Also part of
    // signing out of the admin, so it never throws.
    [END_SUPPORT_SESSION]: async ({ commit, state }: any, payload?: { reason?: 'ended' | 'signed_out' }) => {
      const session: SupportSessionRecord | null = state.supportSession.session;
      if (!session) return true;
      await SupportSessionService.end({ id: session.id, reason: payload?.reason || 'ended' });
      commit(SET_SUPPORT_SESSION, null);
      return true;
    },
    // The app said the session is over: make sure the API has it closed too,
    // and keep the desktop up to say so until it is dismissed.
    [RELEASE_SUPPORT_SESSION]: async ({ state }: any, payload?: { reason?: 'ended' | 'signed_out' }) => {
      const session: SupportSessionRecord | null = state.supportSession.session;
      if (session) await SupportSessionService.end({ id: session.id, reason: payload?.reason || 'ended' });
      return true;
    },
    [DISMISS_SUPPORT_SESSION]: ({ commit }: any) => {
      commit(SET_SUPPORT_SESSION, null);
    },
    // A session kept across a reload: still open? Resolves to whether the
    // desktop comes back. When the API can't be reached it comes back anyway,
    // and the frame finds out.
    [CHECK_SUPPORT_SESSION]: async ({ commit, state }: any) => {
      const session: SupportSessionRecord | null = state.supportSession.session;
      if (!session) return false;
      if (!session.expiresAt || new Date(session.expiresAt).getTime() <= Date.now()) {
        commit(SET_SUPPORT_SESSION, null);
        return false;
      }
      let open = true;
      try {
        const result: AxiosResponse = await SupportSessionService.get(session.id);
        if (result && result.data) {
          open = Boolean(result.data.status && result.data.data && result.data.data.isOpen);
        }
      } catch (error) {
        // Unreachable: keep it.
      }
      // Ended or replaced while this was loading: nothing more to do.
      if (state.supportSession.session !== session) return false;
      if (!open) {
        commit(SET_SUPPORT_SESSION, null);
        return false;
      }
      commit(SET_SUPPORT_SESSION_VERIFIED, true);
      return true;
    },
  },
};

export default supportSession;
